/**
 * BarChart — recharts-powered grouped bar chart.
 * Maintains the same external interface as the previous SVG implementation.
 */
import {
  ResponsiveContainer,
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts'
import type { CSSProperties } from 'react'
import type { ChartSeries } from './LineChart'

interface Props {
  series:   ChartSeries[]
  formatX?: (x: string) => string
  formatY?: (y: number) => string
  yUnit?:   string
  noData?:  string
}

export default function BarChart({ series, formatX, formatY, yUnit = '', noData = '—' }: Props) {
  const allX = [...new Set(series.flatMap(s => s.data.map(d => d.x)))].sort()

  if (allX.length === 0) return <p style={css.empty}>{noData}</p>

  const data = allX.map(x => {
    const row: Record<string, string | number | null> = { x }
    for (const ser of series) {
      row[ser.id] = ser.data.find(d => d.x === x)?.y ?? null
    }
    return row
  })

  const maxY = Math.max(...series.flatMap(s => s.data.map(d => d.y ?? 0)))
  const yDomainMax = maxY > 0 ? Math.ceil(maxY * 1.2) : 10

  const fmtY = (v: number) => (formatY ? formatY(v) : String(v)) + yUnit

  return (
    <div style={css.wrapper}>
      <ResponsiveContainer width="100%" height={220}>
        <ReBarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#ede9e3" vertical={false} />
          <XAxis
            dataKey="x"
            tickFormatter={formatX}
            tick={{ fontSize: 11, fill: '#aaa' }}
            tickLine={false}
            axisLine={{ stroke: '#d8d0c8' }}
          />
          <YAxis
            domain={[0, yDomainMax]}
            tickFormatter={fmtY}
            tick={{ fontSize: 11, fill: '#aaa' }}
            tickLine={false}
            axisLine={false}
            width={48}
          />
          <Tooltip
            formatter={(value, name) => {
              const v = typeof value === 'number' ? fmtY(value) : String(value)
              const ser = series.find(s => s.id === String(name))
              return [v, ser?.label ?? String(name)]
            }}
            labelFormatter={(label) => formatX ? formatX(String(label)) : String(label)}
            contentStyle={{ fontSize: 13, borderRadius: 8, border: '1px solid #e8e0d5' }}
          />
          {series.map(ser => (
            <Bar
              key={ser.id}
              dataKey={ser.id}
              name={ser.id}
              fill={ser.color}
              fillOpacity={0.82}
              radius={[3, 3, 0, 0]}
            />
          ))}
        </ReBarChart>
      </ResponsiveContainer>
    </div>
  )
}

const css: Record<string, CSSProperties> = {
  wrapper: { width: '100%' },
  empty:   { textAlign: 'center', color: '#bbb', padding: '48px 0', fontSize: '1rem', margin: 0 },
}
